import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Container, Grid } from "@material-ui/core";

import HeaderClientes from "components/_refactor/headers/clients";
import FilterCard from "components/_refactor/filter-card";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import PeriodField, {
  DEFAULT_PERIOD_OPTION,
} from "components/_refactor/fields/period-field";
import InputText from "components/_refactor/text/input";
import LoadingData from "components/Loading/LoadingData";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";
import { IsMobile900, IsMobile } from "utils/is-mobile";

import {
  buildRazaoSocialOption,
  mapColetasHistoricoPageFromApi,
  mapLojaFromApi,
  mapProdutosReferenciaCatalogoFromApi,
  unwrapApiResult,
} from "../monitoramento-preco/components/mpMappers";
import DialogDetalheColeta from "./components/dialog-detalhe-coleta";
import TableColetas from "./components/table-coletas";
import buildColetasQueryParams from "./utils/buildColetasQueryParams";
import exportColetasXlsx from "./utils/exportColetasXlsx";
import fetchAllColetas from "./utils/fetchAllColetas";
import mapColetaTableRow from "./utils/mapColetaTableRow";
import { COLETAS_EXPORT_MAX } from "./utils/coletas-export-constants";

const DEFAULT_PAGE_SIZE = 25;

const TIPO_COLETA_OPCOES = [
  { value: "padrao", label: "Padrão" },
  { value: "direcionada", label: "Direcionada" },
];

function getDefaultPeriodDates() {
  const today = moment().format("DD-MM-YYYY");
  const initial = moment().subtract(14, "days").format("DD-MM-YYYY");
  return { dataInicial: initial, dataFinal: today };
}

function createEmptyFilters() {
  return {
    ...getDefaultPeriodDates(),
    tipo: null,
    loja: null,
    produto: null,
    sku: "",
  };
}

export default function HistoricoColetasMonitoramentoPreco() {
  const snackbar = useSnackbar();
  const fetchSeqRef = useRef(0);
  const filtersRef = useRef(createEmptyFilters());

  const isMobile = IsMobile();
  const isMobile900 = IsMobile900();
  const itemXS = isMobile ? 12 : isMobile900 ? 6 : 4;

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [detalheSelecionado, setDetalheSelecionado] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [periodo, setPeriodo] = useState(DEFAULT_PERIOD_OPTION);
  const [filters, setFilters] = useState(createEmptyFilters);
  const [lojasCatalogo, setLojasCatalogo] = useState([]);
  const [produtosCatalogo, setProdutosCatalogo] = useState([]);

  filtersRef.current = filters;

  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  pageRef.current = page;
  pageSizeRef.current = pageSize;

  const lojasOptions = useMemo(
    () =>
      lojasCatalogo.map((loja) => {
        const opt = buildRazaoSocialOption(loja.razaoSocial, loja.cnpj);
        const grupo = loja.grupoEconomico?.trim();
        return {
          ...loja,
          lojaKey: `${loja.idPessoaLoja}-${loja.idGrupoEconomico}`,
          label: grupo
            ? `${opt.razaoSocialLabel} (${grupo})`
            : opt.razaoSocialLabel,
        };
      }),
    [lojasCatalogo],
  );

  const produtosOptions = useMemo(
    () =>
      produtosCatalogo.map((produto) => ({
        ...produto,
        label: produto.nome,
      })),
    [produtosCatalogo],
  );

  const fetchColetas = useCallback(
    async (pageArg, pageSizeArg, filterState = filtersRef.current) => {
      const seq = ++fetchSeqRef.current;
      setLoading(true);

      try {
        const query = buildColetasQueryParams(filterState, {
          page: pageArg,
          pageSize: pageSizeArg,
        });
        const res = await Api.get("coletas", "priceMonitoring", query, true);

        if (seq !== fetchSeqRef.current) return;

        const mapped = mapColetasHistoricoPageFromApi(unwrapApiResult(res));
        setRows(mapped.items.map(mapColetaTableRow));
        setTotalCount(mapped.totalCount);
        setPage(mapped.page);
        setPageSize(mapped.pageSize);
        setHasLoaded(true);
      } catch (error) {
        if (seq !== fetchSeqRef.current) return;

        setRows([]);
        setTotalCount(0);
        const msg =
          typeof error === "string"
            ? error
            : error?.message || "Não foi possível carregar as coletas.";
        snackbar.showMessage(msg, { severity: "error" });
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [snackbar],
  );

  const loadFilterOptions = async () => {
    setLoadingOptions(true);
    try {
      const [lojasRes, prodRes] = await Promise.all([
        Api.get("lojas", "priceMonitoring", "", true),
        Api.get("produtos-referencia", "priceMonitoring", "", true),
      ]);
      const lojasRaw = unwrapApiResult(lojasRes);
      const prodRaw = unwrapApiResult(prodRes);
      setLojasCatalogo(
        Array.isArray(lojasRaw) ? lojasRaw.map(mapLojaFromApi) : [],
      );
      setProdutosCatalogo(
        mapProdutosReferenciaCatalogoFromApi(prodRaw).produtos ?? [],
      );
    } catch {
      snackbar.showMessage(
        "Não foi possível carregar opções de loja e produto.",
        { severity: "warning" },
      );
      setLojasCatalogo([]);
      setProdutosCatalogo([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleSearch = () => {
    fetchColetas(1, pageSizeRef.current, filtersRef.current);
  };

  const handleRefresh = () => {
    fetchColetas(pageRef.current, pageSizeRef.current, filtersRef.current);
  };

  const handleStopSearching = () => {
    fetchSeqRef.current += 1;
    setLoading(false);
  };

  const handleClearFilters = () => {
    const empty = createEmptyFilters();
    setFilters(empty);
    setPeriodo(DEFAULT_PERIOD_OPTION);
    filtersRef.current = empty;
    setHasLoaded(false);
    setRows([]);
    setTotalCount(0);
  };

  const handleTableChange = (type, { page: nextPage, sizePerPage }) => {
    if (type !== "pagination") return;
    fetchColetas(nextPage, sizePerPage, filtersRef.current);
  };

  const handlePeriodoChange = (option) => {
    setPeriodo(option);
    if (option && option.value !== "Outros") {
      const days = Number(option.value);
      const today = moment().format("DD-MM-YYYY");
      const initial = moment()
        .subtract(days - 1, "days")
        .format("DD-MM-YYYY");
      setFilters((prev) => ({
        ...prev,
        dataInicial: initial,
        dataFinal: today,
      }));
    } else if (option?.value === "Outros") {
      setFilters((prev) => ({
        ...prev,
        dataInicial: "",
        dataFinal: "",
      }));
    }
  };

  const handleExport = async () => {
    if (exporting || totalCount === 0) return;

    setExporting(true);
    setExportProgress({ loaded: 0, total: totalCount });

    try {
      const {
        items,
        totalCount: total,
        truncated,
      } = await fetchAllColetas(filtersRef.current, {
        onProgress: ({ loaded, total: progressTotal }) => {
          setExportProgress({
            loaded,
            total: progressTotal ?? totalCount,
          });
        },
      });

      if (!items.length) {
        snackbar.showMessage("Não há coletas para exportar.", {
          severity: "info",
        });
        return;
      }

      exportColetasXlsx(items, { truncated });

      if (truncated) {
        snackbar.showMessage(
          `Exportação limitada a ${COLETAS_EXPORT_MAX} de ${total} registros.`,
          { severity: "warning" },
        );
      } else {
        snackbar.showMessage("Planilha exportada com sucesso.", {
          severity: "success",
        });
      }
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || "Não foi possível exportar as coletas.";
      snackbar.showMessage(msg, { severity: "error" });
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  useEffect(() => {
    loadFilterOptions();
    fetchColetas(1, DEFAULT_PAGE_SIZE, createEmptyFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carga inicial
  }, []);

  return (
    <>
      <HeaderClientes
        section="Histórico de Coletas"
        subsection="Coleta de Preços"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <FilterCard
          handleSearch={handleSearch}
          disabled={loading || loadingOptions}
          searching={loading}
          handleClearFilters={handleClearFilters}
        >
          <Grid item xs={itemXS}>
            <AutoCompleteField
              label="Loja"
              value={filters.loja}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, loja: value }))
              }
              options={lojasOptions}
              optionLabelKey="label"
              optionIdKey="lojaKey"
              disabled={loading || loadingOptions}
              loading={loadingOptions}
            />
          </Grid>
          <Grid item xs={itemXS}>
            <AutoCompleteField
              label="Produto"
              value={filters.produto}
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  produto: value,
                  sku: value ? "" : prev.sku,
                }))
              }
              options={produtosOptions}
              optionLabelKey="label"
              optionIdKey="id"
              disabled={loading || loadingOptions}
              loading={loadingOptions}
            />
          </Grid>
          <Grid item xs={itemXS}>
            <InputText
              label="SKU"
              value={filters.sku}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sku: e.target.value.slice(0, 30),
                }))
              }
              disabled={loading || loadingOptions || !!filters.produto}
              placeholder="Filtrar por SKU"
            />
          </Grid>
          <Grid item xs={itemXS}>
            <AutoCompleteField
              label="Tipo de coleta"
              value={filters.tipo}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, tipo: value }))
              }
              options={TIPO_COLETA_OPCOES}
              optionLabelKey="label"
              optionIdKey="value"
              disabled={loading || loadingOptions}
            />
          </Grid>

          <Grid item xs={12} lg={periodo?.value === "Outros" ? 12 : 4}>
            <PeriodField
              label="Período de envio"
              value={periodo}
              onChange={handlePeriodoChange}
              initialDate={filters.dataInicial}
              finalDate={filters.dataFinal}
              setInitialDate={(value) =>
                setFilters((prev) => ({ ...prev, dataInicial: value || "" }))
              }
              setFinalDate={(value) =>
                setFilters((prev) => ({ ...prev, dataFinal: value || "" }))
              }
              disabled={loading || loadingOptions}
              datePattern="br"
            />
          </Grid>
        </FilterCard>

        <Grid item xs={12}>
          <LoadingData
            searching={loading}
            onClick={handleStopSearching}
            label="Buscando dados..."
          />
        </Grid>

        <Grid item xs={12}>
          {!loading && hasLoaded && (
            <TableColetas
              data={rows}
              totalRegistros={totalCount}
              page={page}
              pageSize={pageSize}
              onTableChange={handleTableChange}
              onRefresh={handleRefresh}
              onViewDetails={setDetalheSelecionado}
              onExport={handleExport}
              exporting={exporting}
              exportProgress={exportProgress}
            />
          )}
        </Grid>
      </Container>
      <DialogDetalheColeta
        open={!!detalheSelecionado}
        onClose={() => setDetalheSelecionado(null)}
        coleta={detalheSelecionado}
      />
    </>
  );
}
